/**
 * EventFlow AI design system.
 *
 * Single source of truth for colors, typography, spacing, radius and shadows.
 * Every component reads from these tokens — never hardcode hex values in JSX.
 * The legacy `t` export in components/ui.tsx now re-exports from here, so
 * existing pages adopt the new palette without per-file edits.
 */

// Colors
export const COLORS = {
  // Brand
  primary: "#6366f1",        // indigo, primary actions and active states
  primaryHover: "#818cf8",   // lighter indigo on hover
  primaryDeep: "#4f46e5",    // pressed / darker indigo
  primarySoft: "#eef2ff",    // soft indigo wash for backgrounds
  primaryGlow: "rgba(99, 102, 241, 0.12)",

  // Surface (light premium theme)
  bg: "#f8f9fb",             // warm off-white page background
  surface: "#ffffff",        // cards, modals
  surfaceHover: "#f3f4f8",   // subtle hover over cards
  surfaceSunken: "#f0f1f5",  // inputs, code, sunken areas

  // Sidebar (dark contrast à la Linear/Vercel)
  sidebarBg: "#12131a",
  sidebarBgGradient: "linear-gradient(180deg, #1a1c28 0%, #12131a 100%)",
  sidebarText: "#e2e4ed",
  sidebarTextMuted: "#9ca3af",
  sidebarBorder: "#252734",
  sidebarHover: "rgba(255, 255, 255, 0.04)",
  sidebarActiveBg: "rgba(99, 102, 241, 0.18)",
  sidebarActiveText: "#ffffff",
  sidebarGlow: "0 0 16px rgba(99, 102, 241, 0.25)",

  // Borders
  border: "#e2e5eb",
  borderStrong: "#cbd0d9",
  borderFocus: "#6366f1",

  // Text
  text: "#1a1d2e",           // near-black for primary text
  textSecondary: "#6b7280",  // body labels, descriptions
  textMuted: "#9ca3af",      // hints, timestamps, weak labels
  textOnDark: "#ffffff",

  // Semantic
  success: "#10b981",
  successSoft: "#d1fae5",
  warning: "#f59e0b",
  warningSoft: "#fef3c7",
  danger: "#ef4444",
  dangerSoft: "#fee2e2",
  info: "#3b82f6",
  infoSoft: "#dbeafe",

  // Accents used in older code
  accent: "#6366f1",
  purple: "#7c3aed",
  cyan: "#0891b2",
} as const;

// Typography — Geist (Vercel) for a modern editorial feel, Geist Mono
// for data/numbers. Both loaded from Google Fonts in index.html.
export const FONTS = {
  sans: "'Geist', 'Inter', system-ui, sans-serif",
  mono: "'Geist Mono', 'JetBrains Mono', ui-monospace, monospace",
} as const;

export const FONT_WEIGHTS = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

// Sizes in px (we use plain numbers; the older code applied them directly)
export const SIZES = {
  xs: 11,
  sm: 12,
  base: 13,
  md: 14,
  lg: 16,
  xl: 18,
  h3: 20,
  h2: 24,
  h1: 28,
  display: 36,
} as const;

export const LETTER_SPACING = {
  tight: "-0.02em",
  normal: "0",
  wide: "0.04em",
  wider: "0.06em",
  widest: "0.08em",
} as const;

// Spacing — multiples of 4
export const SPACE = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  "2xl": 24,
  "3xl": 32,
  "4xl": 48,
} as const;

// Radius
export const RADIUS = {
  sm: 6,
  md: 8,
  lg: 12,
  xl: 16,
  pill: 999,
} as const;

// Shadows
export const SHADOWS = {
  none: "none",
  card: "0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)",
  cardHover: "0 4px 12px rgba(0,0,0,0.08)",
  popover: "0 12px 32px rgba(15, 23, 42, 0.16)",
  modal: "0 24px 60px rgba(15, 23, 42, 0.24)",
  focusRing: "0 0 0 3px rgba(99, 102, 241, 0.22)",
} as const;

export const TRANSITIONS = {
  fast: "120ms cubic-bezier(0.2, 0.8, 0.2, 1)",
  base: "180ms cubic-bezier(0.2, 0.8, 0.2, 1)",
  slow: "280ms cubic-bezier(0.2, 0.8, 0.2, 1)",
} as const;

// Single combined tokens object — what existing code consumes via `t`.
// Keys mirror the legacy `t` shape from components/ui.tsx so nothing breaks.
export const t = {
  // Surfaces
  bg: COLORS.bg,
  surface: COLORS.surface,
  surfaceHover: COLORS.surfaceHover,
  surfaceSunken: COLORS.surfaceSunken,

  // Sidebar
  sidebarBg: COLORS.sidebarBg,
  sidebarBgGradient: COLORS.sidebarBgGradient,
  sidebarText: COLORS.sidebarText,
  sidebarTextMuted: COLORS.sidebarTextMuted,
  sidebarBorder: COLORS.sidebarBorder,
  sidebarHover: COLORS.sidebarHover,
  sidebarActiveBg: COLORS.sidebarActiveBg,
  sidebarActiveText: COLORS.sidebarActiveText,

  // Borders + text
  border: COLORS.border,
  borderStrong: COLORS.borderStrong,
  text: COLORS.text,
  muted: COLORS.textSecondary,
  textMuted: COLORS.textMuted,

  // Brand
  accent: COLORS.primary,
  accentHover: COLORS.primaryHover,
  accentDeep: COLORS.primaryDeep,
  accentSoft: COLORS.primarySoft,
  accentGlow: COLORS.primaryGlow,

  // Semantic
  success: COLORS.success,
  successSoft: COLORS.successSoft,
  warning: COLORS.warning,
  warningSoft: COLORS.warningSoft,
  danger: COLORS.danger,
  dangerSoft: COLORS.dangerSoft,
  info: COLORS.info,
  infoSoft: COLORS.infoSoft,
  purple: COLORS.purple,
  cyan: COLORS.cyan,

  // Radius (strings to keep backwards compat with style={{ borderRadius: t.radius }})
  radius: `${RADIUS.lg}px`,
  radiusSm: `${RADIUS.sm}px`,
  radiusMd: `${RADIUS.md}px`,
  radiusLg: `${RADIUS.lg}px`,
  radiusXl: `${RADIUS.xl}px`,
  radiusPill: `${RADIUS.pill}px`,

  // Type
  font: FONTS.mono,
  fontSans: FONTS.sans,
  fontMono: FONTS.mono,

  // Shadows
  shadow: SHADOWS.card,
  shadowHover: SHADOWS.cardHover,
  shadowPopover: SHADOWS.popover,
  shadowModal: SHADOWS.modal,
  focusRing: SHADOWS.focusRing,

  // Transitions
  transition: TRANSITIONS.base,
  transitionFast: TRANSITIONS.fast,
} as const;

// Deterministic per-string hue used for avatar / tag chips.
export function hueFromString(s: string): number {
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(hash) % 360;
}

/** Soft pastel HSL fill paired with a darker readable text color. */
export function chipFromString(s: string): { bg: string; text: string } {
  const h = hueFromString(s);
  return { bg: `hsl(${h} 85% 94%)`, text: `hsl(${h} 55% 32%)` };
}

/** Avatar background: more saturated than chip, white text on top. */
export function avatarColorFromString(s: string): string {
  const h = hueFromString(s);
  return `hsl(${h} 62% 56%)`;
}
